import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Modal from '@/components/common/modal';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import type { ProcessingInsightDto } from '@/types/dtos/media/processing-insight.dto';
import type { MediaProcessingInstructionsContext } from '@/types/media-processing-instructions.types';
import { buildMediaProcessingInstructionsCopyText } from '@/helpers/media-processing-instructions.helper';
import { copyPlainTextSync } from '@/helpers/clipboard.helper';

type MediaProcessingInstructionsModalProps = {
  open: boolean;
  onClose: () => void;
  insight: ProcessingInsightDto;
  context: MediaProcessingInstructionsContext;
};

export function MediaProcessingInstructionsModal({
  open,
  onClose,
  insight,
  context,
}: MediaProcessingInstructionsModalProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const isDesktop = useBreakpointUp('sm');
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [clipboardFailCount, setClipboardFailCount] = useState(0);

  const copyText = useMemo(() => buildMediaProcessingInstructionsCopyText(context, insight, t), [context, insight, t]);

  const handleCopy = useCallback(() => {
    const container = modalContentRef.current ?? document.body;
    if (copyPlainTextSync(copyText, container)) {
      setClipboardFailCount(0);
      notification.notifySuccess(t('media.processing.instructionsModal.copySuccess'));
      return;
    }
    setClipboardFailCount((count) => count + 1);
    notification.notifyError(t('media.processing.instructionsModal.copyError'));
  }, [copyText, notification, t]);

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={t('media.processing.instructionsModal.title')}
      subtitle={t('media.processing.instructionsModal.subtitle')}
      size="md"
      width={640}
      maxHeight={isDesktop ? '85vh' : '100dvh'}
      fullScreen={!isDesktop}
      isolateClickPropagation
      contentSx={{ overflowY: 'auto' }}
      footer={
        <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{t('common.close')}</Button>
          <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
            {t('media.processing.instructionsModal.copyButton')}
          </Button>
        </Stack>
      }
    >
      <div ref={modalContentRef}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Typography
              component="pre"
              variant="body2"
              sx={{
                m: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
                flex: 1,
                ...(clipboardFailCount >= 2 ? { userSelect: 'all', cursor: 'text' } : {}),
              }}
            >
              {copyText}
            </Typography>
            <Tooltip title={t('media.processing.instructionsModal.copyButton')}>
              <IconButton
                size="small"
                aria-label={t('media.processing.instructionsModal.copyButton')}
                onClick={handleCopy}
                sx={{ mt: -0.5, mr: -0.5, flexShrink: 0 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>
      </div>
    </Modal>
  );
}
